import type { ComputedRef, Ref } from 'vue'
import type { MessagesComponents as components } from '@core/types'
import { computed, onMounted, ref, useDayjs, useTimeoutFn } from '#imports'

export interface UseFlashProgressReturn {
    progressReady: Ref<boolean>
    progressStyle: ComputedRef<{ '--progress-width': string, '--progress-duration': string }>
    dismiss: () => void
}

export function useFlashProgress(
    notification: components['schemas']['RenderedNotification'],
    onDismiss: () => void,
): UseFlashProgressReturn {
    const dayjs = useDayjs()

    const progressScale = ref(1)
    const progressDuration = ref(0)
    const progressReady = ref(false)
    const dismissDelay = ref(0)
    const { start: startDismissTimer, stop: stopDismissTimer } = useTimeoutFn(dismiss, dismissDelay, { immediate: false })
    let dismissed = false

    const progressStyle = computed(() => ({
        '--progress-width': `${progressScale.value * 100}%`,
        '--progress-duration': `${progressDuration.value}s`,
    }))

    function dismiss() {
        stopDismissTimer()
        if (dismissed) return
        dismissed = true
        onDismiss()
    }

    function initializeExpiryProgress() {
        if (!notification.expires_at) return

        const created = dayjs(notification.displayed_created_at ?? notification.created_at)
        const expires = dayjs(notification.expires_at)
        const totalMs = expires.diff(created)
        const remainingMs = expires.diff(dayjs())

        if (totalMs <= 0 || remainingMs <= 0) {
            dismiss()
            return
        }

        dismissDelay.value = remainingMs
        startDismissTimer()

        progressScale.value = remainingMs / totalMs
        progressDuration.value = remainingMs / 1000
        progressReady.value = true
    }

    onMounted(initializeExpiryProgress)

    return {
        progressReady,
        progressStyle,
        dismiss,
    }
}
